/**
 * Сетка месяца для календаря задач (DEV-1592).
 *
 * Неделя начинается с понедельника: так живёт рабочая неделя и так же считает
 * остальной трекер. Дни соседних месяцев в сетке остаются — иначе первая и
 * последняя недели рвались бы посреди строки.
 */

/** День сетки: дата, её место в месяце и задачи, у которых на неё срок */
export interface CalendarDay<T> {
  date: Date
  /** Число месяца — то, что печатается в углу клетки */
  day: number
  /** Из соседнего месяца: клетка приглушена и задач в ней не показываем */
  outside: boolean
  today: boolean
  items: T[]
}

/** Ключ дня в местном поясе: `2026-08-11`. UTC-срез уводил бы срок на сутки. */
export const dayKey = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${date.getFullYear()}-${month}-${day}`
}

/** Тот же ключ из строки времени сервера; пусто — срока нет или он битый */
export const dueKey = (iso: string): string => {
  if (!iso) return ''

  const at = new Date(iso)

  return Number.isNaN(at.getTime()) ? '' : dayKey(at)
}

const startOfMonthGrid = (month: Date): Date => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1)
  // getDay(): воскресенье — 0, а неделя у нас начинается с понедельника.
  const shift = (first.getDay() + 6) % 7

  return new Date(first.getFullYear(), first.getMonth(), 1 - shift)
}

/**
 * Недели месяца — по семь дней, начиная с понедельника. Строк ровно столько,
 * сколько нужно этому месяцу: пустая шестая строка занимала бы шестую часть
 * экрана ни на что.
 */
export const monthGrid = <T>(
  month: Date,
  byDay: Map<string, T[]>,
  today = new Date(),
): CalendarDay<T>[][] => {
  const start = startOfMonthGrid(month)
  const weeks: CalendarDay<T>[][] = []
  const todayKey = dayKey(today)

  for (let week = 0; week < 6; week += 1) {
    const row: CalendarDay<T>[] = []

    for (let index = 0; index < 7; index += 1) {
      const date = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + week * 7 + index,
      )
      const outside = date.getMonth() !== month.getMonth()

      row.push({
        date,
        day: date.getDate(),
        outside,
        today: dayKey(date) === todayKey,
        items: outside ? [] : (byDay.get(dayKey(date)) ?? []),
      })
    }

    // Строка целиком из соседнего месяца в сетке не нужна.
    if (row.every((day) => day.outside)) break

    weeks.push(row)
  }

  return weeks
}

/** Соседний месяц: шаг стрелок в шапке */
export const shiftMonth = (month: Date, step: number): Date =>
  new Date(month.getFullYear(), month.getMonth() + step, 1)
